import { Button } from '@amore/ui/components/button'
import { DatePicker } from '@amore/ui/components/date-picker'
import { Field } from '@amore/ui/components/field'
import { Input } from '@amore/ui/components/input'
import { Textarea } from '@amore/ui/components/textarea'
import { TimePicker } from '@amore/ui/components/time-picker'
import {
  BedDouble,
  Camera,
  Check,
  Heart,
  type LucideIcon,
  Music,
  Plus,
  Sparkles,
  Tag,
  UtensilsCrossed,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import type { Venue } from '@/mock/venues'
import { useSuggestions } from './model'

const dateLabels = {
  placeholder: 'Bez data',
  title: 'Vybrat datum',
  close: 'Zavřít kalendář',
  previousMonth: 'Předchozí měsíc',
  nextMonth: 'Další měsíc',
  clear: 'Bez data',
  today: 'Dnes',
  weekdays: ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'],
}
const timeLabels = {
  open: 'Vybrat čas',
  title: 'Vybrat čas',
  close: 'Zavřít výběr času',
  hours: 'Hodiny',
  minutes: 'Minuty',
  clear: 'Bez času',
  confirm: 'Potvrdit čas',
  format: '24hodinový formát · HH:mm',
}

const useIcons: Record<string, LucideIcon> = {
  Obřad: Heart,
  'Hostina a oslava': UtensilsCrossed,
  Přípravy: Sparkles,
  Ubytování: BedDouble,
  Focení: Camera,
  Afterparty: Music,
}

/** "Co se tady odehraje?": toggle chips like Štítky, plus the couple's own use. */
export function VenueUses({
  value,
  onChange,
  canEdit,
  custom,
  onCustomChange,
}: {
  value: string[]
  onChange: (uses: string[]) => void
  canEdit: boolean
  custom: string
  onCustomChange: (value: string) => void
}) {
  const [naming, setNaming] = useState(false)
  const customRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (naming) customRef.current?.focus()
  }, [naming])
  const options = [...new Set([...useSuggestions, ...value])]
  const shown = canEdit ? options : value
  const toggle = (use: string) =>
    onChange(value.includes(use) ? value.filter((item) => item !== use) : [...value, use])
  const commit = () => {
    const name = custom.trim()
    if (
      name &&
      !value.some((item) => item.toLocaleLowerCase('cs') === name.toLocaleLowerCase('cs'))
    )
      onChange([...value, name])
    onCustomChange('')
    setNaming(false)
  }
  return (
    <section className="space-y-3">
      <div className="space-y-1">
        <h3 className="text-sm font-semibold">Co se tady odehraje?</h3>
        <p className="text-sm text-muted-foreground">Vyberte všechno, co k místu patří.</p>
      </div>
      {shown.length === 0 ? (
        <p className="text-sm text-muted-foreground">Zatím nevybráno</p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {shown.map((use) => {
            const selected = value.includes(use)
            const Icon = useIcons[use] ?? Tag
            return (
              <Button
                key={use}
                size="sm"
                appearance="outline"
                tone="neutral"
                aria-pressed={selected}
                disabled={!canEdit}
                onClick={() => toggle(use)}
              >
                <Icon aria-hidden="true" className="size-3.5" />
                {use}
                {selected && canEdit ? <Check aria-hidden="true" className="size-3.5" /> : null}
              </Button>
            )
          })}
          {!canEdit ? null : naming ? (
            <Input
              ref={customRef}
              value={custom}
              maxLength={80}
              placeholder="Např. Snídaně druhý den"
              aria-label="Vlastní využití"
              className="h-9 w-52 rounded-full px-3.5 text-sm"
              onChange={(event) => onCustomChange(event.target.value)}
              onBlur={commit}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  commit()
                }
                if (event.key === 'Escape') {
                  event.preventDefault()
                  event.stopPropagation()
                  onCustomChange('')
                  setNaming(false)
                }
              }}
            />
          ) : (
            <Button
              size="sm"
              appearance="outline"
              tone="neutral"
              onClick={() => setNaming(true)}
              className="border-dashed text-muted-foreground"
            >
              <Plus aria-hidden="true" className="size-3.5" /> Vlastní
            </Button>
          )}
        </div>
      )}
    </section>
  )
}

export function VenueAgreement({
  draft,
  patch,
  canEdit,
  dateError,
}: {
  draft: Venue
  patch: (patch: Partial<Venue>) => void
  canEdit: boolean
  dateError?: string
}) {
  return (
    <div className="divide-y divide-border [&>*]:py-7 [&>*:first-child]:pt-0 [&>*:last-child]:pb-0">
      <section className="space-y-4">
        <h3 className="text-sm font-semibold">Kdy máte místo k dispozici</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          {(
            [
              { key: 'availableFrom', label: 'Od' },
              { key: 'availableTo', label: 'Do' },
            ] satisfies { key: 'availableFrom' | 'availableTo'; label: string }[]
          ).map(({ key, label }) => (
            <Field key={key} label={label} error={key === 'availableTo' ? dateError : undefined}>
              <DatePicker
                value={draft[key]}
                onValueChange={(value) => patch({ [key]: value ?? '' })}
                readOnly={!canEdit}
                labels={dateLabels}
                className="h-10 border-transparent bg-muted/60 hover:bg-muted"
                formatDate={(date) =>
                  date.toLocaleDateString('cs-CZ', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })
                }
                formatMonth={(date) =>
                  date.toLocaleDateString('cs-CZ', { month: 'long', year: 'numeric' })
                }
              />
            </Field>
          ))}
        </div>
      </section>
      <section className="space-y-4">
        <h3 className="text-sm font-semibold">Předání místa</h3>
        <Field label="Čas předání">
          <TimePicker
            value={draft.handoverTime}
            onValueChange={(value) => patch({ handoverTime: value })}
            disabled={!canEdit}
            labels={timeLabels}
            variant="soft"
            className="h-10 border-transparent bg-muted/60 hover:bg-muted"
          />
        </Field>
        <Field label="Jak se domluvíte na předání">
          <Textarea
            variant="soft"
            className="min-h-24 border-transparent bg-muted/60 hover:bg-muted focus:bg-card"
            rows={3}
            value={draft.handover}
            readOnly={!canEdit}
            maxLength={2000}
            placeholder="Kdo vám místo předá, kde budou klíče a kdy ho vracíte…"
            onChange={(event) => patch({ handover: event.target.value })}
          />
        </Field>
      </section>
      <div className="lg:col-span-2">
        <VenueTextFields
          draft={draft}
          patch={patch}
          canEdit={canEdit}
          fields={[
            {
              key: 'included',
              label: 'Co místo zajistí',
              placeholder: 'Např. stoly, židle, úklid nebo vybavení…',
            },
            {
              key: 'restrictions',
              label: 'Pravidla a omezení',
              placeholder: 'Večerka, svíčky, vlastní catering…',
            },
          ]}
        />
      </div>
    </div>
  )
}

type TextField = {
  key:
    | 'included'
    | 'restrictions'
    | 'parking'
    | 'access'
    | 'accessibility'
    | 'accommodation'
    | 'notes'
  label: string
  placeholder: string
}

export function VenueTextFields({
  draft,
  patch,
  canEdit,
  fields,
}: {
  draft: Venue
  patch: (patch: Partial<Venue>) => void
  canEdit: boolean
  fields: TextField[]
}) {
  return (
    <div className="space-y-5">
      {fields.map(({ key, label, placeholder }) => (
        <div key={key}>
          <Field label={label}>
            <Textarea
              variant="soft"
              className="min-h-24 border-transparent bg-muted/60 hover:bg-muted focus:bg-card"
              rows={3}
              value={draft[key]}
              readOnly={!canEdit}
              maxLength={3000}
              placeholder={canEdit ? placeholder : 'Zatím nedoplněno'}
              onChange={(event) => patch({ [key]: event.target.value })}
            />
          </Field>
        </div>
      ))}
    </div>
  )
}
